import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronDown, FaChevronRight, FaEye, FaUserSlash, FaUserCheck, FaEnvelope, FaTrash, FaShieldAlt } from 'react-icons/fa';
import { apiFetch } from '../../utils/apiAdmin';
import { adminSessionUsable } from '../../utils/authAdmin';
import { useAdminWebSocket } from '../../hooks/useProjectWebSocket';
import { validateProjectMailbox, MAILBOX_DOMAIN } from '../../utils/projectMailbox';

/**
 * ⛔ DEBOUNCED, AND BOTH REFETCHES ON THIS PAGE HIT COGNITO PER ROW (LIV-11).
 *
 * `fetchClients` is `listLeads`: a full-table scan plus one `AdminGetUser` per
 * won lead. `fetchUsers` is `listRosterForAdmin`: one `AdminGetUser` per member,
 * because the members table is a CANDIDATE LIST and every candidate is confirmed
 * against that user's own immutable Cognito `custom:projectId`.
 *
 * Both would otherwise run per message, per open admin tab, and the writers
 * burst - Won writes the lead row and the members row, an invite writes both, a
 * first login writes members.
 *
 * ⚠️ `AdminGetUser` shares the pool's per-account rate quota with SIGN-IN, so a
 * burst across a few open admin tabs can throttle real client logins.
 */
const LIVE_REFRESH_DEBOUNCE_MS = 500;

const STATUS_BADGE = {
    active: 'bg-green-100 text-green-800 border-green-300',
    invited: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    disabled: 'bg-red-100 text-red-800 border-red-300',
    'no-login': 'bg-gray-100 text-gray-600 border-gray-300'
};

const ClientManagement = () => {
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    // projectId -> bool, persisted (project rule: pages keep state across reloads)
    const [expanded, setExpanded] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('adminClientMgmtExpanded')) || {};
        } catch {
            return {};
        }
    });
    const [usersByProject, setUsersByProject] = useState({}); // projectId -> users[] | 'loading'
    const [busyUser, setBusyUser] = useState(null);        // leadId currently acted on
    const [loadError, setLoadError] = useState('');

    // Maintenance mode toggle (public site + client login gate)
    const [maintenanceMode, setMaintenanceMode] = useState(null); // null = not yet loaded
    const [maintenanceBusy, setMaintenanceBusy] = useState(false);

    useEffect(() => {
        const loadMaintenance = async () => {
            try {
                const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/maintenance`);
                if (response.ok) {
                    const data = await response.json();
                    setMaintenanceMode(data.maintenanceMode === true);
                }
            } catch (err) {
                console.error('Failed to load maintenance status:', err);
            }
        };
        loadMaintenance();
    }, []);

    const toggleMaintenance = async () => {
        const turningOn = !maintenanceMode;
        const message = turningOn
            ? 'Turn maintenance mode ON?\n\nThe public website and client portal login will show a maintenance page. The admin portal stays available.'
            : 'Turn maintenance mode OFF?\n\nThe public website and client portal login will be restored.';
        if (!window.confirm(message)) return;

        setMaintenanceBusy(true);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/maintenance`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ maintenanceMode: turningOn })
            });
            if (response.ok) {
                setMaintenanceMode(turningOn);
            } else {
                const data = await response.json().catch(() => ({}));
                alert(data.error || 'Failed to update maintenance mode');
            }
        } catch (err) {
            console.error('Failed to update maintenance mode:', err);
            alert('Failed to update maintenance mode');
        } finally {
            setMaintenanceBusy(false);
        }
    };
    const [removeTarget, setRemoveTarget] = useState(null); // { user, projectId }
    const [removeConfirmEmail, setRemoveConfirmEmail] = useState('');
    const [removeError, setRemoveError] = useState('');

    // ⚠️ SILENT MODE on all three loaders (LIV, D1). The message path passes
    // `{ silent: true }`: no loading state, no error banner, and the data on
    // screen KEPT on failure. These are mount-time loaders and every one of
    // them breaks D1's "silent refresh" if called as it stands.
    const fetchClients = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoadError('');
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/leads`);
            if (response.ok) {
                const data = await response.json();
                setClients((data.leads || []).filter(l => l.status === 'won' && l.projectId));
            } else if (!silent) {
                setLoadError(`Failed to load clients (${response.status})`);
            } else {
                console.warn('Background client refresh failed - keeping the list on screen');
            }
        } catch (err) {
            console.error('Failed to fetch clients:', err);
            if (!silent) setLoadError('Failed to load clients');
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    // ⛔ The silent half here is not cosmetic: `'loading'` makes the users table
    // AND its controls vanish, so a background refresh would empty a card the
    // operator is working in, mid-interaction. And the failure paths set `[]`,
    // which would blank the roster on a transient blip.
    const fetchUsers = useCallback(async (projectId, { silent = false } = {}) => {
        if (!silent) setUsersByProject(prev => ({ ...prev, [projectId]: 'loading' }));
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/project-users/${projectId}`);
            if (response.ok) {
                const data = await response.json();
                setUsersByProject(prev => ({ ...prev, [projectId]: data.users || [] }));
            } else if (!silent) {
                setUsersByProject(prev => ({ ...prev, [projectId]: [] }));
            } else {
                console.warn('Background roster refresh failed - keeping the users on screen');
            }
        } catch (err) {
            console.error('Failed to fetch users:', err);
            if (!silent) setUsersByProject(prev => ({ ...prev, [projectId]: [] }));
        }
    }, []);

    // Project mailbox per won client (PROJECT-MAILBOX D4/D7/T493). Lives on
    // the PROJECT row, so it has its own route - the card's existing saves
    // write the lead row and Cognito. leadId -> { draft, saved, error, busy }
    const [mailboxes, setMailboxes] = useState({});

    const fetchMailbox = useCallback(async (leadId, { silent = false } = {}) => {
        if (!silent) setMailboxes(prev => ({ ...prev, [leadId]: { ...(prev[leadId] || {}), busy: true } }));
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/project-mailbox/${leadId}`);
            const data = await response.json().catch(() => ({}));
            if (silent && !response.ok) {
                // Keep whatever is on screen; a background failure says nothing.
                console.warn('Background mailbox refresh failed - keeping the field as it is');
                return;
            }
            setMailboxes(prev => ({
                ...prev,
                [leadId]: response.ok
                    ? { draft: data.projectMailbox || '', saved: data.projectMailbox || '', error: '', busy: false }
                    : { draft: '', saved: '', error: data.error || 'Failed to load mailbox', busy: false }
            }));
        } catch (err) {
            console.error('Failed to fetch project mailbox:', err);
            if (!silent) {
                setMailboxes(prev => ({ ...prev, [leadId]: { draft: '', saved: '', error: 'Failed to load mailbox', busy: false } }));
            }
        }
    }, []);

    const saveMailbox = async (leadId) => {
        const entry = mailboxes[leadId] || {};
        // Convenience check only - the backend validator is the guard.
        const validationError = validateProjectMailbox(entry.draft);
        if (validationError) {
            setMailboxes(prev => ({ ...prev, [leadId]: { ...entry, error: validationError } }));
            return;
        }
        setMailboxes(prev => ({ ...prev, [leadId]: { ...entry, busy: true, error: '' } }));
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/project-mailbox/${leadId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ projectMailbox: entry.draft })
            });
            const data = await response.json().catch(() => ({}));
            setMailboxes(prev => ({
                ...prev,
                [leadId]: response.ok
                    ? { draft: data.projectMailbox, saved: data.projectMailbox, error: '', busy: false }
                    : { ...entry, busy: false, error: data.error || `Failed to save (${response.status})` }
            }));
        } catch (err) {
            console.error('Failed to save project mailbox:', err);
            setMailboxes(prev => ({ ...prev, [leadId]: { ...entry, busy: false, error: 'Failed to save mailbox' } }));
        }
    };

    const toggleExpand = (projectId, leadId) => {
        const opening = !expanded[projectId];
        setExpanded(prev => {
            const next = { ...prev, [projectId]: opening };
            localStorage.setItem('adminClientMgmtExpanded', JSON.stringify(next));
            return next;
        });
        if (opening && !Array.isArray(usersByProject[projectId])) {
            fetchUsers(projectId);
        }
        if (opening && leadId && mailboxes[leadId] === undefined) {
            fetchMailbox(leadId);
        }
    };

    // Cards restored as expanded from a previous session need their users
    // (and their project mailbox) loaded
    useEffect(() => {
        clients.forEach(client => {
            if (expanded[client.projectId] && usersByProject[client.projectId] === undefined) {
                fetchUsers(client.projectId);
            }
            if (expanded[client.projectId] && mailboxes[client.id] === undefined) {
                fetchMailbox(client.id);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clients]);

    // -----------------------------------------------------------------------
    // Live updates (LIV-11). THREE refetches, not one.
    //
    // ⛔ `fetchClients` alone would have repainted nothing D2 promised. It loads
    // the client LIST; the users table inside each card is `fetchUsers`, and the
    // effect that calls it fires only when that project's entry is `undefined` -
    // so it never re-runs for a card already open, which is every card the
    // operator is looking at. Add, remove, role, disable and enable - the five
    // things D2 calls live - would not have repainted at all. Both fetchUsers
    // and fetchMailbox are therefore called DIRECTLY from here, past that guard.
    //
    // ✅ A role change, a disable and an enable ARE covered: all three write the
    // members row (admin-api sets and removes `disabled`, and sets `role`), and
    // the members table streams. Cognito stays the source of truth for the role;
    // the mirrored column is not read as authority.
    //
    // ⚠️ NOT live, by decision (D2): an MFA enrolment or a resent invitation.
    // Both are the CLIENT's action, at their end, so the MFA shield goes stale
    // until a reload.
    // -----------------------------------------------------------------------

    // Read in the message handler, which must not re-subscribe on every change.
    const clientsRef = useRef(clients);
    const expandedRef = useRef(expanded);
    useEffect(() => { clientsRef.current = clients; }, [clients]);
    useEffect(() => { expandedRef.current = expanded; }, [expanded]);

    // GATE 3 - the file's own flags, plus a Project mailbox field being edited.
    // ⚠️ `maintenanceBusy` is judged and EXCLUDED: it toggles a site-wide
    // setting and holds no list data, so refreshing the cards beneath it
    // changes nothing the operator is editing.
    const mailboxDirty = Object.values(mailboxes).some(
        entry => entry && (entry.busy || (entry.draft || '') !== (entry.saved || '')));
    const liveRefreshBlocked = Boolean(busyUser || removeTarget || removeConfirmEmail || mailboxDirty);
    const liveRefreshBlockedRef = useRef(liveRefreshBlocked);
    useEffect(() => { liveRefreshBlockedRef.current = liveRefreshBlocked; }, [liveRefreshBlocked]);

    // D11 - what a gate-3 refusal holds. The pending set IS the coalescing: ten
    // messages for one project leave one entry, whether they were held by gate 3
    // or by the debounce.
    const pendingRef = useRef({ clients: false, users: new Set(), mailboxes: new Set() });
    const refreshTimerRef = useRef(null);

    // GATE 0 lives HERE - at the point of refetch, once per coalesced flush -
    // not per message in the socket handler (Fable, round 2). There it renewed
    // the session for message types this page ignores, and the deferred flush
    // reached apiFetch, and its redirect, with no gate at all. Checked BEFORE
    // the pending set is taken, so a session that cannot be renewed leaves it
    // held for the next signal rather than dropping it (D11).
    const flushPending = useCallback(async () => {
        if (!await adminSessionUsable()) return;
        const pending = pendingRef.current;
        pendingRef.current = { clients: false, users: new Set(), mailboxes: new Set() };
        if (pending.clients) fetchClients({ silent: true });
        pending.users.forEach(projectId => fetchUsers(projectId, { silent: true }));
        pending.mailboxes.forEach(leadId => fetchMailbox(leadId, { silent: true }));
    }, [fetchClients, fetchUsers, fetchMailbox]);

    const scheduleFlush = useCallback(() => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(() => {
            refreshTimerRef.current = null;
            flushPending();
        }, LIVE_REFRESH_DEBOUNCE_MS);
    }, [flushPending]);

    useEffect(() => () => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    }, []);

    // The deferred flush: whatever was held runs once, when the flag clears.
    useEffect(() => {
        if (liveRefreshBlocked) return;
        const pending = pendingRef.current;
        if (!pending.clients && pending.users.size === 0 && pending.mailboxes.size === 0) return;
        scheduleFlush();
    }, [liveRefreshBlocked, scheduleFlush]);

    useAdminWebSocket(useCallback((message) => {
        // GATE 0 is inside flushPending, at the point of refetch.
        // GATE 1 - type.
        const { type } = message;
        if (type !== 'leads_updated' && type !== 'roster_updated' && type !== 'project_row_updated') return;

        if (type === 'leads_updated') {
            pendingRef.current.clients = true;
        } else {
            // GATE 2 - a SET test, not a single id: this page shows every won
            // client, so it accepts a message for any project it is rendering.
            const client = clientsRef.current.find(c => c.projectId === message.projectId);
            if (!client) return;

            if (type === 'roster_updated') {
                pendingRef.current.clients = true;
                if (expandedRef.current[client.projectId]) {
                    pendingRef.current.users.add(client.projectId);
                }
            } else if (expandedRef.current[client.projectId]) {
                // project_row_updated. Only for an OPEN card: a closed one
                // renders no mailbox field, and opening it fetches anyway.
                pendingRef.current.mailboxes.add(client.id);
            }
        }

        // GATE 3 - hold it. The flush effect above picks it up when the flag
        // clears, so nothing is dropped.
        if (liveRefreshBlockedRef.current) return;
        scheduleFlush();
    }, [scheduleFlush]));

    const act = async (projectId, user, method, pathSuffix, body) => {
        setBusyUser(user.leadId);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/users/${user.leadId}${pathSuffix}`, {
                method,
                headers: { 'Content-Type': 'application/json' },
                ...(body ? { body: JSON.stringify(body) } : {})
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                alert(data.error || `Action failed (${response.status})`);
            } else if (data.inviteResent) {
                alert('User re-enabled. Their invite email was re-sent (previous temporary password had expired).');
            }
            await fetchUsers(projectId);
            return response.ok;
        } catch (err) {
            console.error('User action failed:', err);
            alert('Action failed');
            return false;
        } finally {
            setBusyUser(null);
        }
    };

    const emailMatches = (typed, actual) =>
        typed.trim().toLowerCase() === String(actual || '').trim().toLowerCase();

    const handleRemove = async () => {
        if (!removeTarget) return;
        setRemoveError('');
        if (!emailMatches(removeConfirmEmail, removeTarget.user.email)) {
            setRemoveError('Email does not match');
            return;
        }
        const ok = await act(removeTarget.projectId, removeTarget.user, 'DELETE', '', { confirmEmail: removeTarget.user.email });
        if (ok) {
            setRemoveTarget(null);
            setRemoveConfirmEmail('');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 overflow-y-auto">
        <div className="container-width py-8">
        <div className="mb-8 flex justify-between items-center">
        <h1 className="text-3xl font-light text-primary-900">
        Client <span className="font-normal">Management</span>
        </h1>
        {maintenanceMode !== null && (
            <div className="flex items-center gap-3">
            <span className={`text-sm px-3 py-1 rounded-full ${
                maintenanceMode
                ? 'bg-red-100 text-red-800 border border-red-300'
                : 'bg-green-100 text-green-800 border border-green-300'
            }`}>
            {maintenanceMode ? 'Maintenance ON' : 'Site Live'}
            </span>
            <button
            onClick={toggleMaintenance}
            disabled={maintenanceBusy}
            className={`text-sm px-4 py-2 rounded border disabled:opacity-50 ${
                maintenanceMode
                ? 'bg-green-600 text-white border-green-700 hover:bg-green-700'
                : 'bg-red-600 text-white border-red-700 hover:bg-red-700'
            }`}
            >
            {maintenanceBusy ? 'Updating…' : maintenanceMode ? 'Turn Maintenance OFF' : 'Turn Maintenance ON'}
            </button>
            </div>
        )}
        </div>

        {loading ? (
            <p className="text-gray-600">Loading clients…</p>
        ) : loadError ? (
            <p className="text-red-600">{loadError}</p>
        ) : clients.length === 0 ? (
            <p className="text-gray-600">No won clients yet.</p>
        ) : (
            <div className="space-y-4">
            {clients.map(client => {
                const users = usersByProject[client.projectId];
                const isOpen = expanded[client.projectId];
                return (
                    <div key={client.id} className="bg-white rounded-lg shadow">
                    <div
                    className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleExpand(client.projectId, client.id)}
                    >
                    <div className="flex items-center gap-3">
                    {isOpen ? <FaChevronDown className="text-gray-400" size={14} /> : <FaChevronRight className="text-gray-400" size={14} />}
                    <span className="font-semibold text-primary-900 uppercase">{client.company}</span>
                    <span className="text-gray-600 text-sm">{client.name}</span>
                    {Array.isArray(users) && (
                        <span className="text-gray-500 text-sm">{users.length} user{users.length === 1 ? '' : 's'}</span>
                    )}
                    </div>
                    <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/admin/client-view/${client.id}`); }}
                    className="text-sm px-3 py-1.5 bg-gray-100 text-gray-600 border border-gray-300 rounded hover:bg-gray-200 flex items-center gap-2"
                    >
                    <FaEye size={13} />
                    Open board
                    </button>
                    </div>

                    {isOpen && (
                        <div className="px-6 pb-5">
                        {(() => {
                            const mailbox = mailboxes[client.id] || {};
                            const dirty = (mailbox.draft ?? '') !== (mailbox.saved ?? '');
                            return (
                                <div className="mb-4 pb-4 border-b border-gray-100">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                Project mailbox
                                </label>
                                <div className="flex items-center gap-2">
                                <input
                                type="text"
                                value={mailbox.draft ?? ''}
                                placeholder={`clientname@${MAILBOX_DOMAIN}`}
                                title="The shared mailbox that receives a copy of every email about this project"
                                onChange={(e) => setMailboxes(prev => ({
                                    ...prev,
                                    [client.id]: { ...(prev[client.id] || {}), draft: e.target.value, error: '' }
                                }))}
                                className="flex-1 max-w-md px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                                <button
                                onClick={() => saveMailbox(client.id)}
                                disabled={mailbox.busy || !dirty}
                                className="text-xs px-3 py-1.5 border border-primary-300 text-primary-700 rounded hover:bg-primary-50 disabled:opacity-50"
                                >
                                {mailbox.busy ? 'Saving…' : 'Save'}
                                </button>
                                </div>
                                {mailbox.error && <p className="text-red-600 text-xs mt-1">{mailbox.error}</p>}
                                {!mailbox.error && !mailbox.busy && !dirty && mailbox.saved && (
                                    <p className="text-green-600 text-xs mt-1">Saved</p>
                                )}
                                {!mailbox.error && !mailbox.busy && !mailbox.saved && mailbox.saved !== undefined && !dirty && (
                                    <p className="text-yellow-700 text-xs mt-1">
                                    Not set - emails about this project cannot reach a project mailbox until it is.
                                    </p>
                                )}
                                </div>
                            );
                        })()}
                        {users === 'loading' || users === undefined ? (
                            <p className="text-gray-500 text-sm py-2">Loading users…</p>
                        ) : users.length === 0 ? (
                            <p className="text-gray-500 text-sm py-2">No users found for this project.</p>
                        ) : (
                            <table className="w-full text-sm">
                            <thead>
                            <tr className="text-left text-gray-500 border-b border-gray-200">
                            <th className="py-2 pr-4 font-medium">Name</th>
                            <th className="py-2 pr-4 font-medium">Email</th>
                            <th className="py-2 pr-4 font-medium">Role</th>
                            <th className="py-2 pr-4 font-medium">Status</th>
                            <th className="py-2 pr-4 font-medium">MFA</th>
                            <th className="py-2 font-medium">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {users.map(user => (
                                <tr key={user.leadId} className={`border-b border-gray-100 ${user.status === 'disabled' ? 'text-gray-400' : ''}`}>
                                <td className="py-2 pr-4">{user.name}</td>
                                <td className="py-2 pr-4">{user.email}</td>
                                <td className="py-2 pr-4">
                                <select
                                value={user.role || 'user'}
                                disabled={busyUser === user.leadId}
                                onChange={(e) => act(client.projectId, user, 'PUT', '/role', { role: e.target.value })}
                                className="border border-gray-300 rounded px-2 py-1 text-sm"
                                >
                                <option value="admin">admin</option>
                                <option value="user">user</option>
                                </select>
                                </td>
                                <td className="py-2 pr-4">
                                <span className={`px-2 py-0.5 rounded-full border text-xs ${STATUS_BADGE[user.status] || STATUS_BADGE['no-login']}`}>
                                {user.status}
                                </span>
                                </td>
                                <td className="py-2 pr-4">
                                <span className={`flex items-center gap-1 text-xs ${user.mfaEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                                <FaShieldAlt size={11} />
                                {user.mfaEnabled ? 'on' : 'off'}
                                </span>
                                </td>
                                <td className="py-2">
                                <div className="flex items-center gap-2">
                                {!user.invitedBy ? (
                                    <span className="text-xs text-gray-400 italic" title="The primary contact is removed with the project via lead delete">
                                    Primary contact
                                    </span>
                                ) : user.status === 'disabled' ? (
                                    <button
                                    onClick={() => act(client.projectId, user, 'PUT', '/enable')}
                                    disabled={busyUser === user.leadId}
                                    className="text-xs px-2 py-1 border border-green-300 text-green-700 rounded hover:bg-green-50 flex items-center gap-1 disabled:opacity-50"
                                    >
                                    <FaUserCheck size={11} /> Enable
                                    </button>
                                ) : (
                                    <button
                                    onClick={() => act(client.projectId, user, 'PUT', '/disable')}
                                    disabled={busyUser === user.leadId}
                                    className="text-xs px-2 py-1 border border-red-300 text-red-700 rounded hover:bg-red-50 flex items-center gap-1 disabled:opacity-50"
                                    >
                                    <FaUserSlash size={11} /> Disable
                                    </button>
                                )}
                                {user.status === 'invited' && (
                                    <button
                                    onClick={() => act(client.projectId, user, 'POST', '/resend-invite')}
                                    disabled={busyUser === user.leadId}
                                    className="text-xs px-2 py-1 border border-gray-300 text-gray-600 rounded hover:bg-gray-50 flex items-center gap-1 disabled:opacity-50"
                                    >
                                    <FaEnvelope size={11} /> Resend
                                    </button>
                                )}
                                {user.invitedBy && user.status === 'active' && (
                                    <button
                                    onClick={async () => {
                                        if (!window.confirm(`Make ${user.name || user.email} the primary contact?\n\nThe client record transfers to them and they become a client administrator. The current primary becomes a normal user (disable/remove separately if they have left).`)) return;
                                        const ok = await act(client.projectId, user, 'POST', '/make-primary');
                                        if (ok) fetchClients();
                                    }}
                                    disabled={busyUser === user.leadId}
                                    className="text-xs px-2 py-1 border border-primary-300 text-primary-700 rounded hover:bg-primary-50 flex items-center gap-1 disabled:opacity-50"
                                    >
                                    Make Primary
                                    </button>
                                )}
                                {user.invitedBy && (
                                    <button
                                    onClick={() => { setRemoveTarget({ user, projectId: client.projectId }); setRemoveConfirmEmail(''); setRemoveError(''); }}
                                    disabled={busyUser === user.leadId}
                                    className="text-xs px-2 py-1 border border-red-300 text-red-700 rounded hover:bg-red-50 flex items-center gap-1 disabled:opacity-50"
                                    >
                                    <FaTrash size={11} /> Remove
                                    </button>
                                )}
                                </div>
                                </td>
                                </tr>
                            ))}
                            </tbody>
                            </table>
                        )}
                        </div>
                    )}
                    </div>
                );
            })}
            </div>
        )}

        {/* Remove confirmation - typed email */}
        {removeTarget && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Remove user</h2>
            <p className="text-gray-600 text-sm mb-4">
            This permanently removes <strong>{removeTarget.user.name}</strong>'s login and
            clears their task assignments and pending sign-offs. Their comments and history
            stay. This cannot be undone — to suspend instead, use Disable.
            </p>
            <p className="text-gray-700 text-sm mb-2">
            Type <strong>{removeTarget.user.email}</strong> to confirm:
            </p>
            <input
            type="text"
            value={removeConfirmEmail}
            onChange={(e) => setRemoveConfirmEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {removeError && <p className="text-red-600 text-sm mb-3">{removeError}</p>}
            <div className="flex gap-3">
            <button
            onClick={handleRemove}
            disabled={busyUser === removeTarget.user.leadId || !emailMatches(removeConfirmEmail, removeTarget.user.email)}
            className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 disabled:opacity-50"
            >
            Remove user
            </button>
            <button
            onClick={() => { setRemoveTarget(null); setRemoveConfirmEmail(''); }}
            className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
            Cancel
            </button>
            </div>
            </div>
            </div>
        )}
        </div>
        </div>
    );
};

export default ClientManagement;
